import { CLASS_NAMES, SELECTORS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for the Donut module
 */
export default class Donut extends MessageBus {
  /**
   * Constructor function of the Donut class. Must receive one argument, a DOM
   * node representing a donut chart.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    /**
     * DOM node that is passed into the constructor
     * @property {object} element DOM node that is passed into the constructor
     */
    this.element = element;

    // Initialize the view
    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers and
   * then create the event listeners
   * @returns {object} Donut A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();
    return this;
  }

  /**
   * Cache DOM References
   *
   * Find all necessary DOM elements used in the view and cache them
   * @returns {object} Donut A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.donutPies = this.element.querySelectorAll(`.${CLASS_NAMES.DONUT_PIE}`);
    return this;
  }

  /**
   * Create event handlers to enable interaction with view
   * @returns {object} Donut A reference to the current instance of the class
   * @chainable
   */
  enable() {
    if (this.donutPies.length === 0) return this;

    this.donutPies.forEach((pie) => {
      const observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          observer.unobserve(pie);
          this.animateDonut(pie);
        }
      });

      observer.observe(pie);
    });

    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {object} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    return this;
  }

  /**
   * Animate the donut chart
   * @param {HTMLElement} element - the donut chart element
   */
  animateDonut(element) {
    const targetPctsRaw = element
      .getAttribute(SELECTORS.DATA_TARGET_PCTS)
      .split(',');
    let cumulativeTotal = 0;

    const targets = targetPctsRaw
      .map((pct) => {
        const parsedPct = parseInt(pct, 10); // Added radix parameter
        if (parsedPct) {
          cumulativeTotal += parsedPct;
          return cumulativeTotal;
        }
        return null;
      })
      .filter((pct) => pct !== null)
      .reverse();

    const arcCount = targets.length;
    const arcOffset = 5 - arcCount;
    targets.forEach((target, idx) => {
      const arcIdx = arcOffset + idx;
      const arc = element.querySelector(
        `${SELECTORS.SVG_CIRCLE}(${arcIdx + 1})`
      );

      this.animateArc(arc, target, 1.5 * (target / 100));
    });
  }

  /**
   * Custom function to animate the arc
   * @param {HTMLElement} arc - the arc element
   * @param {number} target - the target percentage
   * @param {number} duration - the duration of the animation
   * @returns {object} Donut A reference to the current instance of the class
   * @chainable
   */
  animateArc(arc, target, duration) {
    const start = performance.now();
    const initialLen = 0;
    const finalLen = (1570.796 / 100) * target;

    const animate = (time) => {
      const elapsed = time - start;
      const progress = Math.min(elapsed / (duration * 1000), 1);
      const currentLen = initialLen + (finalLen - initialLen) * progress;
      arc.setAttribute(
        'stroke-dasharray',
        `${currentLen} ${1570.796 - currentLen}`
      );

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);

    return this;
  }
}
